import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import FileBrowserActions from 'file-browser/FileBrowserActions';
import {AaSequenceFileState} from 'FileStates';
import AaSequenceActions from 'molbio/aa-sequences/AaSequenceActions';
import {openSequenceVersion} from 'molbio/SequenceActions';
import BioentityChip from 'ui-library/chips/BioentityChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {SharedSmartChipProps} from 'ui-library/chips/ChipUtil';
import useSmartChipQuery, {SmartChipType} from 'ui-library/chips/useSmartChipQuery';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/**
 * A chip that refers to a specific version of the bioentity.
 * Currently only supported for nucleotide sequences/oligos.
 */
export default function BioentityVersionChip(
  props: SharedSmartChipProps<SmartChipType.BIOENTITY> & {versionId?: number}
): JSX.Element {
  const {item: bioentity} = useSmartChipQuery<SmartChipType.BIOENTITY>({id: props.id});
  if (
    props.id == null ||
    props.versionId == null ||
    bioentity == null ||
    !(bioentity.__typename === 'SequenceNode' || bioentity.__typename === 'AaSequenceNode')
  ) {
    return <BioentityChip {...props} />;
  }

  return (
    <span className={css(styles.wrapper)}>
      <BioentityChip
        {...props}
        onClick={() => {
          if (bioentity.__typename === 'SequenceNode') {
            openSequenceVersion(props.id!, props.versionId!);
          } else if (bioentity.__typename === 'AaSequenceNode') {
            FileBrowserActions.openFile(props.id!, {
              versionId: props.versionId!,
              toolbarTrigger: 'openHistoryPanel',
              onOpen(fileState: AaSequenceFileState) {
                if (fileState.get('protein').getActiveVersionId() !== props.versionId!) {
                  AaSequenceActions.open(props.id!, {versionId: props.versionId!});
                }
              },
            });
          }
        }}
      />
      {bioentity.latestVersionId !== props.versionId && (
        // Per design, render the previous-version icon outside the chip. Some chips already have
        // another icon or action inside on the right, so this indicator is kept as a sibling
        // instead of using the chip's action area.
        <BenchlingIcon
          className={classNames(css(styles.clockIcon), {
            [css(styles.clockIconTableCell)]: props.size === ChipSize.TABLE_CELL,
          })}
          icon="previous-version"
        />
      )}
    </span>
  );
}

const styles = StyleSheet.create({
  wrapper: {display: 'flex'},
  clockIcon: {
    color: GRAY_DARKER,
    margin: 'auto',
    marginLeft: '-4px',
  },
  clockIconTableCell: {marginLeft: '-2px'},
});
